import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress
} from '@mui/material';
import { useLocation } from 'react-router-dom';

/**
 * Public page that allows users to unsubscribe from emails
 */
const Unsubscribe = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [optOutAll, setOptOutAll] = useState(true);
  
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  
  // Verify token and get email
  useEffect(() => {
    const verifyToken = async () => {
      try {
        // In a real app, this would verify the token with the backend
        // and retrieve the associated email address
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (token) {
          // For demo purposes, we're just using a mock email
          setEmail('customer@example.com');
          setLoading(false);
        } else {
          setError('Invalid or missing unsubscribe token');
          setLoading(false);
        }
      } catch (err) {
        setError('An error occurred while processing your request');
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // In a real app, submit to backend
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError('An error occurred while processing your request');
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  if (success) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          You have been successfully unsubscribed.
        </Alert>
        <Typography variant="body1">
          We're sorry to see you go. If you change your mind, you can update your preferences at any time by contacting us.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Email Preferences
        </Typography>
        
        <Typography variant="body1" paragraph>
          You are currently subscribed to emails from Fallen Leaf Marina with the email address: <strong>{email}</strong>
        </Typography>
        
        <FormGroup sx={{ mb: 3 }}>
          <FormControlLabel 
            control={
              <Checkbox 
                checked={optOutAll} 
                onChange={(e) => setOptOutAll(e.target.checked)} 
              />
            } 
            label="Unsubscribe from all marketing emails" 
          />
        </FormGroup>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          fullWidth
        >
          Update Preferences
        </Button>
      </Paper>
    </Box>
  );
};

export default Unsubscribe; 